import { FieldValues } from 'react-hook-form';
import { Extension, QuestionnaireItem } from 'fhir/r4';
import {
  AssessmentsQuestionViewModel,
  AssessmentsQuestionViewModels,
  AssessmentsQuestionsResponse,
  AssessmentsValidationAttributeFieldsResponse,
  AssessmentsTextareaQuestionViewModel,
  AssessmentsRadioQuestionViewModel,
  AssessmentQuestionOption,
  AssessmentsCheckboxQuestionViewModel,
  AssessmentsTextboxQuestionViewModel,
  AssessmentsDateQuestionViewModel,
  AssessmentsUnsupportedQuestionViewModel,
  AssessmentsLabelQuestionViewModel,
  AssessmentsQuestionViewModelBase,
  AssessmentsSelectQuestionViewModel,
  AssessmentsVerifyUserRequest,
  AssessmentAuthenticationFields,
  AssessmentsVerifyUserResponse,
  AssessmentDetails,
  AssessmentStatus,
  AssessmentsQuestionnaireViewModel,
} from '../types';
import {
  ASSESSMENT_ACCESS_VALUE_ACTIVITY_ID,
  ASSESSMENT_ACCESS_VALUE_ACTIVITY_STATUS,
  ASSESSMENT_ACCESS_VALUE_ASSESSMENT_ID,
  ASSESSMENT_ACCESS_VALUE_ASSESSMENT_STATUS,
  ASSESSMENT_ACCESS_VALUE_CLIENT_PATIENT_ID,
  ASSESSMENT_ACCESS_VALUE_FIRST_NAME,
  ASSESSMENT_ACCESS_VALUE_TENANT_ID,
} from '../constants';
import { formatToDashedNational } from '../../../utils/phoneNumberUtils/phoneNumberUtils';

interface ExtensionMetadata {
  optionId?: number;
  optionLabel?: string;
  questionNext?: number;
  isOptionRequired?: boolean;
  isSubOptionRequired?: boolean;
  isDynamic?: boolean;
}

export const transformAssessmentsAuthConfig = (
  response: AssessmentsValidationAttributeFieldsResponse,
) =>
  response.validationAttributes.map((attr) => ({
    key: attr.key,
    type: attr.config.type,
    label: attr.config.label,
  }));

const extractExtensionMetadata = (extensions: Extension[] = []): ExtensionMetadata => {
  if (!extensions) return {}; // return if no extensions

  const metadata: ExtensionMetadata = {
    optionId: undefined,
    optionLabel: undefined,
    questionNext: undefined,
    isOptionRequired: undefined,
    isSubOptionRequired: undefined,
    isDynamic: undefined,
  };
  extensions.forEach((ext) => {
    switch (ext.url) {
      case 'optionId':
        metadata.optionId = ext.valueDecimal;
        break;
      case 'optionLabel':
        metadata.optionLabel = ext.valueString;
        break;
      case 'questionNext':
        metadata.questionNext = ext.valueDecimal;
        break;
      case 'isOptionRequired':
        metadata.isOptionRequired = ext.valueBoolean;
        break;
      case 'isSubOptionRequired':
        metadata.isSubOptionRequired = ext.valueBoolean;
        break;
      case 'isDynamic':
        metadata.isDynamic = ext.valueBoolean;
        break;
      default:
    }
  });
  return metadata; // Return all values at once
};

const getBaseQuestionProps = (
  item: QuestionnaireItem,
  index: number,
): Omit<AssessmentsQuestionViewModelBase, 'type'> & { isDynamic: boolean } => {
  const { optionId, optionLabel, questionNext, isDynamic } = extractExtensionMetadata(
    item?.extension?.[0].extension,
  );

  return {
    id: Number(item.linkId),
    questionNumber: index + 1,
    questionText: item.text ?? '',
    isRequired: item.required ?? false,
    placeholder: optionLabel ?? '',
    questionNext,
    optionId,
    isDynamic: isDynamic ?? false,
  };
};

const transformQuestionnaireItemToUnsupported = (
  baseProps: Omit<AssessmentsQuestionViewModelBase, 'type'>,
): AssessmentsUnsupportedQuestionViewModel => ({
  type: 'unsupported',
  ...baseProps,
});

const transformQuestionnaireItemToTextarea = (
  baseProps: Omit<AssessmentsQuestionViewModelBase, 'type'>,
): AssessmentsTextareaQuestionViewModel => ({
  type: 'textarea',
  ...baseProps,
});

const transformQuestionnaireItemToTextBox = (
  baseProps: Omit<AssessmentsQuestionViewModelBase, 'type'>,
): AssessmentsTextboxQuestionViewModel => ({
  type: 'textbox',
  ...baseProps,
});

const transformQuestionnaireItemToDate = (
  baseProps: Omit<AssessmentsQuestionViewModelBase, 'type'>,
): AssessmentsDateQuestionViewModel => ({
  type: 'date',
  ...baseProps,
});

const transformQuestionnaireItemToDisplay = (
  baseProps: Omit<AssessmentsQuestionViewModelBase, 'type'>,
): AssessmentsLabelQuestionViewModel => ({
  type: 'label',
  ...baseProps,
  isRequired: false, // display type questions cannot be required
});

const buildSubQuestionMap = (
  subItems: QuestionnaireItem[] = [],
): Map<string, QuestionnaireItem[]> => {
  const map = new Map<string, QuestionnaireItem[]>();

  subItems.forEach((subItem) => {
    const parentOptionCode = subItem.enableWhen?.[0]?.answerCoding?.code;

    if (parentOptionCode) {
      if (!map.has(parentOptionCode)) {
        map.set(parentOptionCode, []);
      }
      map.get(parentOptionCode)!.push(subItem);
    }
  });
  return map;
};

const transformQuestionnaireItemToChoice = (
  item: QuestionnaireItem,
  baseProps: Omit<AssessmentsQuestionViewModelBase, 'type'>,
  isSubQuestion = false,
  transformSubQuestions: (items: QuestionnaireItem, index: number) => AssessmentsQuestionViewModel,
):
  | AssessmentsRadioQuestionViewModel
  | AssessmentsCheckboxQuestionViewModel
  | AssessmentsSelectQuestionViewModel
  | AssessmentsUnsupportedQuestionViewModel => {
  const choiceType = item.extension?.[0].valueCodeableConcept?.coding?.[0].code;
  let type: 'checkbox' | 'radio' | 'select' = 'checkbox';

  switch (choiceType) {
    case 'radio-button':
      type = 'radio';
      break;
    case 'check-box':
      type = 'checkbox';
      break;
    case 'drop-down':
      type = 'select';
      break;
    default:
  }

  const subQuestionMap = buildSubQuestionMap(item.item);
  const options: AssessmentQuestionOption[] = [];

  for (let i = 0; i < (item.answerOption?.length ?? 0); i++) {
    const answerOption = item.answerOption?.[i];
    if (answerOption) {
      const filteredSubQuestions = subQuestionMap.get(answerOption.valueCoding?.code ?? '') ?? [];
      const { isDynamic, ...optionBaseProps } = extractExtensionMetadata(
        answerOption.extension?.[0].extension,
      );
      if (isDynamic) {
        return transformQuestionnaireItemToUnsupported(baseProps);
      }
      options.push({
        value: answerOption.valueCoding?.code ?? '',
        label: answerOption.valueCoding?.display ?? '',
        questionNext: optionBaseProps.questionNext,
        isOptionRequired: isSubQuestion
          ? optionBaseProps.isSubOptionRequired
          : optionBaseProps.isOptionRequired,
        subQuestions: filteredSubQuestions.map(transformSubQuestions),
      });
    }
  }

  return {
    type,
    ...baseProps,
    options,
  };
};

function transformQuestionnaireItem(
  item: QuestionnaireItem,
  index: number,
  isSubQuestion = false,
): AssessmentsQuestionViewModel {
  const { isDynamic, ...baseProps } = getBaseQuestionProps(item, index);

  if (isDynamic) {
    return transformQuestionnaireItemToUnsupported(baseProps);
  }

  const transformSubQuestion = (subItem: QuestionnaireItem, subIndex: number) =>
    transformQuestionnaireItem(subItem, subIndex, true);

  switch (item.type) {
    case 'text':
      return transformQuestionnaireItemToTextarea(baseProps);
    case 'choice':
      return transformQuestionnaireItemToChoice(
        item,
        baseProps,
        isSubQuestion,
        transformSubQuestion,
      );
    case 'string':
      return transformQuestionnaireItemToTextBox(baseProps);
    case 'date':
      return transformQuestionnaireItemToDate(baseProps);
    case 'display':
      return transformQuestionnaireItemToDisplay(baseProps);
    default:
      return transformQuestionnaireItemToUnsupported(baseProps);
  }
}

export const transformAssessmentsQuestionsResponse = (
  response: AssessmentsQuestionsResponse,
): AssessmentsQuestionnaireViewModel => {
  const questionnaire = response.questions.entry?.find(
    (entry) => entry.resource?.resourceType === 'Questionnaire',
  )?.resource?.item;

  const questions = questionnaire
    ? questionnaire.map((item: QuestionnaireItem, index: number) =>
        transformQuestionnaireItem(item, index),
      )
    : [];

  return {
    questions,
    surveyId: response.surveyId,
  };
};

export const transformVerifyUserRequest = ({
  authFields,
  authdata,
  surveyEventId,
}: {
  authFields: AssessmentAuthenticationFields;
  authdata: FieldValues;
  surveyEventId: string;
}): AssessmentsVerifyUserRequest =>
  authFields.reduce(
    (acc, { type, key }) => {
      let parameter;
      const value = String(authdata[key]).trim();
      if (type === 'dob') {
        parameter = { name: key, valueDate: value };
      } else if (type === 'phone') {
        // Normalize phone number to xxx-xxx-xxxx format
        const normalizedPhone = formatToDashedNational(value);
        parameter = { name: key, valueString: normalizedPhone };
      } else {
        parameter = { name: key, valueString: value };
      }

      acc.userAttributes?.parameter?.[1]?.part?.push(parameter);
      return acc;
    },
    {
      userAttributes: {
        resourceType: 'Parameters',
        parameter: [
          { name: 'campaignGuid', valueString: surveyEventId },
          { name: 'validation', part: [] },
        ],
      },
    } as AssessmentsVerifyUserRequest,
  );

export const transformVerifyUserResponse = (
  response: AssessmentsVerifyUserResponse,
): AssessmentDetails => {
  const assessmentDetails: Partial<AssessmentDetails> = {
    accessToken: response.accessToken ?? '',
  };

  response.assessmentAccessResult?.parameter?.forEach((param) => {
    switch (param.name) {
      case ASSESSMENT_ACCESS_VALUE_FIRST_NAME:
        assessmentDetails.firstName = param.valueString ?? '';
        break;
      case ASSESSMENT_ACCESS_VALUE_TENANT_ID:
        assessmentDetails.tenantId = param.valueString ?? '';
        break;
      case ASSESSMENT_ACCESS_VALUE_CLIENT_PATIENT_ID:
        assessmentDetails.clientPatientId = param.valueString ?? '';
        break;
      case ASSESSMENT_ACCESS_VALUE_ASSESSMENT_ID:
        assessmentDetails.assessmentsId = param.valueString ?? '';
        break;
      case ASSESSMENT_ACCESS_VALUE_ACTIVITY_ID:
        assessmentDetails.activityId = param.valueString ?? '';
        break;
      case ASSESSMENT_ACCESS_VALUE_ACTIVITY_STATUS:
        assessmentDetails.activityStatus = (param.valueString as AssessmentStatus) ?? null;
        break;
      case ASSESSMENT_ACCESS_VALUE_ASSESSMENT_STATUS:
        assessmentDetails.assessmentStatus = (param.valueString as AssessmentStatus) ?? null;
        break;
      default:
    }
  });

  return assessmentDetails as AssessmentDetails;
};
